import React from 'react';
import QcStatTiles from './QcStatTiles';

// Goods-in QC — delivery detail (design 6a in "Goods-in QC.dc.html").
//
// Replaces the inline row expansion in the Deliveries table: clicking a
// delivery now opens a full screen for it. One delivery is often several QC
// sittings and a few hundred units, which the expansion could not carry without
// pushing the rest of the table off screen.
//
// Everything here is derived from the stored QC session snapshots on the batch
// (see `qcBatches` in App.js), never recomputed from st.devices, so the page
// still reads correctly after units have been moved, repaired or retired. Where
// the design wants a figure the app has no source for — units ordered on the PO,
// an agreed QC deadline — the panel says what it is showing instead of
// inventing one.
//
// Props:
//   batch     one entry from qcBatches
//   onBack    return to the Deliveries list
//   onOpenRma jump to the RMA screen (null hides the button)
//   onExport  CSV export for this delivery (null hides the button)
export default function QcDeliveryDetail({ batch, onBack, onOpenRma, onExport }) {
  const [unitFilter, setUnitFilter] = React.useState('all');
  const [showAllUnits, setShowAllUnits] = React.useState(false);

  // Flatten every sitting into one unit list, carrying the tester and date from
  // the session the unit was checked in. Failures lead: on a 300-unit delivery
  // the handful that failed are the actionable part.
  const units = React.useMemo(() => {
    const flat = (batch.sessions || []).flatMap(sess =>
      (sess.devices || []).map(u => ({
        ...u,
        byName: sess.byName || 'Unknown',
        checkedAt: u.checkedAt || sess.finishedAt || null,
      }))
    );
    return flat.sort((a, b) => (a.result === 'pass' ? 1 : 0) - (b.result === 'pass' ? 1 : 0));
  }, [batch.sessions]);

  const failUnits = units.filter(u => u.result !== 'pass');
  const passUnits = units.filter(u => u.result === 'pass');

  // Per-check results across the whole delivery. `checks` is an id -> pass/fail/na
  // map recorded per unit, so the counts here are exactly what was recorded at
  // the bench rather than a recount against today's QC definitions (which may
  // since have been edited in Settings).
  const checkResults = React.useMemo(() => {
    const byId = new Map();
    const labelFor = new Map();
    units.forEach(u => {
      Object.entries(u.checks || {}).forEach(([id, res]) => {
        if (!byId.has(id)) byId.set(id, { id, checked: 0, fails: 0 });
        const row = byId.get(id);
        if (res === 'pass' || res === 'fail') row.checked++;
        if (res === 'fail') row.fails++;
      });
      // Only failing checks carry a human label in the snapshot, and they are
      // stored in check order, so pair them up to name what we can.
      const failedIds = Object.entries(u.checks || {}).filter(([, r]) => r === 'fail').map(([id]) => id);
      (u.failedChecks || []).forEach((label, i) => {
        if (failedIds[i] && !labelFor.has(failedIds[i])) labelFor.set(failedIds[i], label);
      });
    });
    return [...byId.values()].map(r => ({ ...r, label: labelFor.get(r.id) || r.id }));
  }, [units]);

  const filtered = unitFilter === 'pass' ? passUnits
    : unitFilter === 'fail' ? failUnits
      : units;
  const shown = showAllUnits ? filtered : filtered.slice(0, 12);

  const fmt = d => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
  const fmtShort = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—');

  const blocked = batch.live.staged > 0 && batch.rmaRefs.length === 0;
  const cleared = batch.fail === 0 || (batch.live.staged === 0 && batch.rmaRefs.length > 0);
  const stage = blocked ? 'Blocked' : cleared ? 'Cleared' : 'In check';
  const tone = blocked ? 'danger' : cleared ? 'success' : 'info';

  // The first sitting carries the setup the batch was started with; later
  // sittings on the same import batch reuse it.
  const first = [...(batch.sessions || [])]
    .sort((a, b) => new Date(a.finishedAt || 0) - new Date(b.finishedAt || 0))[0] || {};
  const firmwares = [...new Set(units.map(u => u.firmware).filter(Boolean))];

  const setupFacts = [
    ['Device type', batch.types.join(', ') || '—'],
    ['Supplier', 'Lightbug'],
    ['SIM 1', first.simType || '—'],
    ['SIM 2', first.simType2 || '—'],
    ['Firmware', firmwares.length
      ? firmwares.slice(0, 3).join(', ') + (firmwares.length > 3 ? ' +' + (firmwares.length - 3) : '')
      : '—'],
    ['Purchase date', first.purchaseDate ? fmt(first.purchaseDate) : '—'],
    ['Units checked', String(batch.total)],
  ];

  // The app records a finished sitting, not the pause/resume beats the design
  // sketches, so the timeline is one entry per session, newest first.
  const events = [...(batch.sessions || [])]
    .sort((a, b) => new Date(b.finishedAt || 0) - new Date(a.finishedAt || 0))
    .map(s => {
      const n = (s.devices || []).length;
      const f = (s.devices || []).filter(d => d.result !== 'pass').length;
      return {
        id: s.id,
        text: n + ' unit' + (n === 1 ? '' : 's') + ' checked' + (f ? ' — ' + f + ' failed' : ', all passed'),
        meta: (s.finishedAt
          ? new Date(s.finishedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
          : 'In progress') + ' · ' + (s.byName || 'Unknown'),
        dot: f ? 'var(--danger-text)' : 'var(--success-text)',
      };
    });

  const unitFilters = [
    { key: 'all', label: 'All', count: units.length },
    { key: 'pass', label: 'Passed', count: passUnits.length },
    { key: 'fail', label: 'Failed', count: failUnits.length },
  ];

  const maxFail = batch.topFails.length ? batch.topFails[0][1] : 0;

  const card = { background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' };
  const cardHead = { height: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)' };
  const cardTitle = { fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' };
  const unitCols = '120px 72px 116px minmax(0,1fr) 140px 90px';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button onClick={onBack} aria-label="Back to deliveries"
          style={{ width: 32, height: 32, flexShrink: 0, border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--surface-default)', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="tnum" style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.3px', color: 'var(--text-primary)' }}>{batch.batch}</span>
            {batch.types.map(t => (
              <span key={t} style={{ display: 'inline-flex', fontSize: 11.5, fontWeight: 500, padding: '2px 8px', borderRadius: 4, background: 'var(--surface-inset)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }}>{t}</span>
            ))}
            <span className={'pill pill-' + tone + ' pill-dot'}>{stage}</span>
          </div>
          <div style={{ fontSize: 14, lineHeight: '18px', color: 'var(--text-muted)', marginTop: 4 }}>
            {batch.total} unit{batch.total === 1 ? '' : 's'} checked
            {batch.firstAt ? ', first on ' + fmt(batch.firstAt) : ''}
            {batch.testers.length ? ' · ' + batch.testers.join(', ') : ''}
          </div>
        </div>
        {onExport && (
          <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={onExport}>Export CSV</button>
        )}
        {onOpenRma && batch.fail > 0 && (
          <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={onOpenRma}>
            {batch.rmaRefs.length ? 'View RMA' : 'Raise RMA for this delivery'}
          </button>
        )}
      </div>

      {/* Tiles. "Still to action" replaces the design's "To check": the app only
          stores finished sittings, so there is no in-flight count — what is
          outstanding on a checked delivery is the failures with no RMA. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,minmax(0,1fr))', gap: 12 }}>
        {[
          { label: 'Checked', value: String(batch.total), sub: batch.sessions.length + ' sitting' + (batch.sessions.length === 1 ? '' : 's') },
          { label: 'Passed', value: String(batch.pass), sub: 'booked into stock', fg: 'var(--success-text)', border: 'var(--success-border)' },
          { label: 'Failed', value: String(batch.fail), sub: batch.fail ? 'held back from stock' : 'none on this delivery', fg: batch.fail ? 'var(--danger-text)' : undefined, border: batch.fail ? 'var(--danger-border)' : undefined },
          { label: 'Still to action', value: String(batch.live.staged), sub: batch.live.staged ? 'awaiting an RMA' : 'nothing outstanding', fg: batch.live.staged ? 'var(--warning-text)' : undefined, border: batch.live.staged ? 'var(--warning-border)' : undefined },
          { label: 'Pass rate', value: batch.rate + '%', sub: batch.fail + ' unit' + (batch.fail === 1 ? '' : 's') + ' failed' },
        ].map(t => (
          <div key={t.label} className="card" style={{ padding: '14px 16px', ...(t.border ? { borderColor: t.border } : {}) }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{t.label}</div>
            <div className="tnum" style={{ fontSize: 28, fontWeight: 500, lineHeight: 1, marginTop: 8, color: t.fg || 'var(--text-primary)' }}>{t.value}</div>
            <div style={{ fontSize: 12, color: t.fg || 'var(--text-muted)', marginTop: 6 }}>{t.sub}</div>
          </div>
        ))}
      </div>

      {/* Pass/fail split bar */}
      <div className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ ...cardTitle, flex: 1 }}>Result</span>
          <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {batch.pass} passed · {batch.fail} failed · {batch.total} checked
          </span>
        </div>
        <div style={{ height: 8, borderRadius: 9999, background: 'var(--surface-inset)', overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: (batch.total ? (batch.pass / batch.total) * 100 : 0) + '%', background: 'var(--success-text)' }} />
          <div style={{ width: (batch.total ? (batch.fail / batch.total) * 100 : 0) + '%', background: 'var(--danger-text)' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          {[['Passed ' + batch.pass, 'var(--success-text)'], ['Failed ' + batch.fail, 'var(--danger-text)']].map(([label, swatch]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: swatch }} />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Two columns, as the design */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 16, alignItems: 'start' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

          {/* Units */}
          <div style={card}>
            <div style={{ ...cardHead, height: 52, flexWrap: 'wrap' }}>
              <span style={{ ...cardTitle, flexShrink: 0 }}>Units</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {unitFilters.map(f => {
                  const on = unitFilter === f.key;
                  return (
                    <button key={f.key} onClick={() => { setUnitFilter(f.key); setShowAllUnits(false); }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7, height: 28, padding: '0 10px', borderRadius: 9999, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                        border: '1px solid ' + (on ? 'var(--brand-border)' : 'var(--border-default)'),
                        background: on ? 'var(--brand-tint)' : 'var(--surface-default)',
                        color: on ? 'var(--brand-light)' : 'var(--text-muted)', fontWeight: on ? 600 : 400,
                      }}>
                      {f.label}<span className="tnum" style={{ fontSize: 11.5, fontWeight: 600 }}>{f.count}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ flex: 1 }} />
              <span className="tnum" style={{ flexShrink: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                showing {shown.length} of {filtered.length}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: unitCols, gap: '0 14px', height: 34, alignItems: 'center', padding: '0 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', fontSize: 11.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
              <div>Serial</div><div>Type</div><div>Status</div><div>Failed check</div><div>Checked by</div><div style={{ textAlign: 'right' }}>When</div>
            </div>

            {shown.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>No units in this view.</div>
            ) : shown.map(u => {
              const failed = u.result !== 'pass';
              return (
                <div key={(u.deviceId || u.serial) + String(u.checkedAt)}
                  style={{ display: 'grid', gridTemplateColumns: unitCols, gap: '0 14px', alignItems: 'center', minHeight: 46, padding: '7px 16px', borderBottom: '1px solid var(--border-subtle)', background: failed ? 'var(--danger-fill)' : 'transparent' }}>
                  <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{u.serial}</div>
                  <div>
                    <span style={{ display: 'inline-flex', fontSize: 11.5, fontWeight: 500, padding: '2px 8px', borderRadius: 4, background: 'var(--surface-inset)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }}>{u.type}</span>
                  </div>
                  <div><span className={'pill pill-' + (failed ? 'danger' : 'success') + ' pill-dot'}>{failed ? 'Failed' : 'Passed'}</span></div>
                  <div style={{ minWidth: 0, fontSize: 14, color: failed ? 'var(--danger-text)' : 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={[(u.failedChecks || []).join(', '), u.note || ''].filter(Boolean).join(' · ')}>
                    {(u.failedChecks || []).join(', ') || '—'}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.byName}</div>
                  <div className="tnum" style={{ textAlign: 'right', fontSize: 14, color: 'var(--text-muted)' }}>{fmtShort(u.checkedAt)}</div>
                </div>
              );
            })}

            {filtered.length > shown.length && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, height: 44, padding: '0 16px', background: 'var(--surface-inset)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Failed units are listed first.</span>
                <button onClick={() => setShowAllUnits(true)} className="btn btn-ghost btn-sm">Show all {filtered.length}</button>
              </div>
            )}
          </div>

          {/* Failures, with their notes — the actionable part of the delivery */}
          {failUnits.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-default)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={cardTitle}>Failures on this delivery</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
                    {failUnits.length} unit{failUnits.length === 1 ? '' : 's'} held back from stock.
                    {batch.rmaRefs.length
                      ? ' On RMA ' + batch.rmaRefs.join(', ') + '.'
                      : ' Keeping them on one RMA keeps the supplier claim traceable to this delivery.'}
                  </div>
                </div>
                {onOpenRma && (
                  <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={onOpenRma}>
                    {batch.rmaRefs.length ? 'Open RMA' : 'Raise one RMA for the batch'}
                  </button>
                )}
              </div>
              {failUnits.map(u => (
                <div key={'f' + (u.deviceId || u.serial) + String(u.checkedAt)}
                  style={{ display: 'grid', gridTemplateColumns: '120px 200px minmax(0,1fr) 150px', gap: '0 14px', alignItems: 'center', minHeight: 46, padding: '7px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{u.serial}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {(u.failedChecks || []).length
                      ? u.failedChecks.map(c => <span key={c} className="pill pill-danger" style={{ fontSize: 11 }}>{c}</span>)
                      : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Marked as failed</span>}
                  </div>
                  <div style={{ minWidth: 0, fontSize: 12, color: 'var(--text-muted)' }}>{u.note || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.byName} · {fmtShort(u.checkedAt)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Failure breakdown, ranked, so the dominant fault is obvious */}
          {batch.topFails.length > 0 && (
            <div style={card}>
              <div style={cardHead}><span style={cardTitle}>Failure breakdown</span></div>
              <div style={{ padding: '12px 16px' }}>
                {batch.topFails.map(([label, n]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>{label}</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 'var(--radius-pill)', background: 'var(--surface-inset)', overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                      <div style={{ width: (maxFail ? Math.round((n / maxFail) * 100) : 0) + '%', height: '100%', background: 'var(--danger-text)' }} />
                    </div>
                    <span className="tnum" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', minWidth: 80, textAlign: 'right' }}>{n} unit{n === 1 ? '' : 's'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Results by check */}
          {checkResults.length > 0 && (
            <div style={card}>
              <div style={cardHead}><span style={cardTitle}>Results by check</span></div>
              {checkResults.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 46, padding: '8px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{c.label}</div>
                    <div className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{c.checked} of {batch.total} units checked</div>
                  </div>
                  <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', width: 88 }}>
                    {c.fails > 0
                      ? <span className="pill pill-danger pill-dot">{c.fails} fail{c.fails === 1 ? '' : 's'}</span>
                      : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>All passed</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* The one place this screen joins back to live device state */}
          <div style={card}>
            <div style={cardHead}><span style={cardTitle}>Where these units are now</span></div>
            <div style={{ padding: 16 }}>
              <QcStatTiles tiles={[
                { label: 'In stock', value: batch.live.available },
                { label: 'On site', value: batch.live.deployed },
                { label: 'RMA', value: batch.live.rma, color: batch.live.rma ? 'var(--danger-text)' : undefined },
                { label: 'Retired', value: batch.live.retired },
              ]} />
              {batch.live.staged > 0 && (
                <div style={{ fontSize: 12, color: 'var(--warning-text)', marginTop: 10 }}>
                  {batch.live.staged} unit{batch.live.staged === 1 ? '' : 's'} sitting at Pending RMA with no shipment raised.
                </div>
              )}
            </div>
          </div>

          {/* Setup */}
          <div style={card}>
            <div style={cardHead}><span style={cardTitle}>Setup this batch was started with</span></div>
            {setupFacts.map(([k, v]) => (
              <div key={k} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{k}</span>
                <span className="tnum" style={{ fontSize: 14, color: 'var(--text-primary)', textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </div>

          {/* QC sittings */}
          <div style={card}>
            <div style={cardHead}><span style={cardTitle}>QC sittings</span></div>
            {events.map((e, i) => (
              <div key={e.id || i} style={{ display: 'flex', gap: 10, padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ width: 7, height: 7, borderRadius: 9999, flexShrink: 0, marginTop: 6, background: e.dot }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, lineHeight: '18px', color: 'var(--text-primary)' }}>{e.text}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{e.meta}</div>
                </div>
              </div>
            ))}
            <div style={{ padding: '12px 16px', background: 'var(--surface-inset)', fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)' }}>
              Passed units were booked into stock when each sitting was finished. Nothing moves from this page.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
